import { Component, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { MeterDetailTableModel } from 'src/app/models/tables/maintenance/meter-detail-table.model';
import { MeterTableModel } from 'src/app/models/tables/maintenance/meter-table.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { MeterDetailService } from 'src/app/services/maintenance/meter-detail.service';
import { MeterService } from 'src/app/services/maintenance/meter.service';

@Component({
  selector: 'app-meter',
  templateUrl: './meter.component.html',
  styleUrl: './meter.component.scss',
  standalone: false,
})
export class MeterComponent {

  private readonly meterService = inject(MeterService);
  private readonly meterDetailService = inject(MeterDetailService);
  private readonly router = inject(Router);
  private readonly headerService = inject(HeaderService);
  private readonly title = inject(Title);

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<MeterDetailTableModel> =
    new PagedResponseModel<MeterDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = []
  generalData: PagedResponseModel<MeterTableModel> =
    new PagedResponseModel<MeterTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined
  limit: number = 10;
  nameWindow: string = 'Captura Medidores';
  page: number = 1;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  url: string = 'meter';

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllMetersWithPagination(),
    ]);
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(
        this.meterDetailService.exportDetailConsultation(
          this.filters, this.columnSort, this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Detalle Medidores.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los detalles de los medidores.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(
        this.meterService.exportGeneralConsultation(
          this.filters, this.columnSort, this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Medidores.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los medidores.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        const response = await firstValueFrom(
          this.meterDetailService.getColumnsForDetailConsultation()
        );
        this.detailHeader = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta de detalles.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        const response = await firstValueFrom(
          this.meterService.getColumnsForGeneralConsultation()
        );
        this.generalHeader = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta general.', error);
    }
  }

  async getAllMetersWithPagination() {
    try {
      const response = await firstValueFrom(
        this.meterService.getAllMetersWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
      this.generalData = response;
    } catch (error) {
      console.error('Error al obtener los medidores.', error);
    }
  }

  async getAllMeterDetailsWithPagination() {
    try {
      const response = await firstValueFrom(
        this.meterDetailService.getAllMeterDetailsWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
      this.detailData = response;
    } catch (error) {
      console.error('Error al obtener los detalles de los medidores.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    if (this.activeTabIndex === tabIndex) return;
    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;
    switch (this.activeTabIndex) {
      case 0: {
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllMetersWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      }
      case 1: {
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllMeterDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      }
    }
  }

  newRegister() {
    this.router.navigate([`/${this.url}/register`]);
  }

  onAction(event: { action: string, item?: MeterTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.meterId}`]);
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllMetersWithPagination()
        break;
      case 1:
        await this.getAllMeterDetailsWithPagination()
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
